import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { readApiJson } from "../utils/apiResponse";

const FIELDS = {
  contacts: ["companyNames", "activities"],
  leads: ["companyNames", "skills", "locations", "owners", "statuses", "activities", "sources"],
  companies: ["companyNames", "skills", "locations", "owners", "statuses", "activities", "sources"],
  clients: ["companyNames", "skills", "locations", "owners", "statuses", "activities", "sources"],
  jobs: ["job_titles", "tech_stacks", "locations", "owners", "statuses", "activities", "sources"],
  candidates: ["names", "skills", "locations", "owners", "statuses", "stages", "sources"],
  content: ["contentTypes", "statuses", "owners", "sources", "dateTerms", "usageStates", "activities", "contentTerms"],
};
const EMPTY_FIELDS = [];
const identity = options => options;
function requestFor(endpoint, draft) {
  const [path, query] = endpoint.split("?");
  const context = Object.fromEntries(new URLSearchParams(query));
  let page = path.replace(/^\/api\//, "");
  if (page === "companies" && context.scope === "client") page = "clients";
  const fields = FIELDS[page.split(":")[0]] || [];
  const aliases = { ...(page === "leads" || page === "companies" || page === "clients" ? {skills:"techKeywords"} : {}),
    contentTypes:"contentType", activities:page.startsWith("content:") ? "activityTerms" : "activities" };
  const selected = Object.fromEntries(fields.map(field => [field, draft[aliases[field] || field] || []]));
  context.search = draft.ai || draft.ai_agent || draft.aiRequest || context.search || "";
  if (context.candidate_stage) context.candidate_stage = context.candidate_stage.split(",");
  return { page, selected, context };
}
export async function fetchPageFilterOptions(body, signal) {
  const base = String(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
  const token = ["token", "authToken", "jwt", "accessToken", "access_token"]
    .map(key => localStorage.getItem(key) || sessionStorage.getItem(key)).find(Boolean);
  const response = await fetch(`${base}/api/filter-options/page`, {
    method:"POST", credentials:"include", signal,
    headers:{"Content-Type":"application/json", ...(token ? {Authorization:`Bearer ${token}`} : {})},
    body:JSON.stringify(body),
  });
  if (response.status === 503) throw Object.assign(new Error("Updating filter values…"), {retry:true});
  return readApiJson(response, "Filter options");
}

// Only bounded option lists cross the network. No source records or browser facet scans.
export default function usePageFilterOptions(open, endpoint, draft = {}, setDraft) {
  const [searches, setSearches] = useState({});
  const [state, setState] = useState({options:{}, loading:false, error:"", more:{}});
  const paging = useRef(new Map());
  const requestKey = JSON.stringify({...requestFor(endpoint, draft), searches});
  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setState(previous => ({...previous, options:{}, key:"", loading:true, error:""}));
    let timer;
    const run = async () => {
      setState(previous => ({...previous, loading:true, error:""}));
      try {
        const result = await fetchPageFilterOptions(JSON.parse(requestKey), controller.signal);
        if (!controller.signal.aborted) setState({...result, loading:false, error:"", key:requestKey});
      } catch (error) {
        if (controller.signal.aborted) return;
        if (error.retry) { timer = setTimeout(run, 1000); return; }
        setState(previous => ({...previous, loading:false, error:error.message}));
      }
    };
    timer = setTimeout(run, 150);
    const pending = paging.current;
    return () => { controller.abort(); clearTimeout(timer); pending.forEach(c => c.abort()); pending.clear(); };
  }, [open, requestKey]);
  useEffect(() => {
    if (!open || state.key !== requestKey || !setDraft || !state.invalidSelected) return;
    setDraft(previous => {
      let next = previous;
      for (const [field, invalid] of Object.entries(state.invalidSelected)) {
        if (!invalid.length) continue;
        const request = JSON.parse(requestKey);
        const alias = field === "skills" && ["leads", "companies", "clients"].includes(request.page) ? "techKeywords"
          : field === "contentTypes" ? "contentType" : field === "activities" && request.page.startsWith("content:") ? "activityTerms" : field;
        const current = previous[alias];
        const bad = new Set(invalid.map(v => v.toLowerCase()));
        const value = Array.isArray(current) ? current.filter(v => !bad.has(v.toLowerCase())) : bad.has(String(current).toLowerCase()) ? "" : current;
        if (JSON.stringify(value) !== JSON.stringify(current)) next = {...next, [alias]:value};
      }
      return next;
    });
  }, [open, requestKey, setDraft, state.key, state.invalidSelected]);
  const search = useCallback((field, text) => {
    setSearches(previous => (previous[field] || "") === text ? previous : {...previous, [field]:text});
  }, []);
  const loadMore = async field => {
    if (!open || state.loading || state.key !== requestKey || !state.more?.[field] || paging.current.has(field)) return;
    const controller = new AbortController();
    paging.current.set(field, controller);
    try {
      const next = await fetchPageFilterOptions({...JSON.parse(requestKey), onlyField:field,
        cursors:{[field]:state.cursors[field]}}, controller.signal);
      if (!controller.signal.aborted) setState(previous => previous.key !== requestKey ? previous : ({...previous,
        options:{...previous.options,[field]:[...new Set([...previous.options[field],...next.options[field]])]},
        more:{...previous.more,[field]:next.more[field]}, cursors:{...previous.cursors,[field]:next.cursors[field]},
      }));
    } catch (error) {
      if (!controller.signal.aborted && !error.retry) setState(previous => ({...previous,error:error.message}));
    } finally { if (paging.current.get(field) === controller) paging.current.delete(field); }
  };
  const page = requestFor(endpoint, {}).page.split(":")[0];
  const fields = FIELDS[page] || EMPTY_FIELDS;
  const callbacks = useMemo(() => Object.fromEntries(fields.map(field => [field, value => search(field, value)])), [fields, search]);
  const options = useMemo(() => Object.fromEntries(fields.map(field => [field,
    state.key === requestKey ? state.options[field] || [] : []])), [fields, state.key, state.options, requestKey]);
  const fieldProps = field => ({
    onInputValueChange: callbacks[field], loading:open && (state.loading || state.key !== requestKey) && !state.error,
    autocompleteProps: {filterOptions:identity, ListboxProps:{onScroll:event => {
      const list = event.currentTarget;
      if (list.scrollHeight - list.scrollTop - list.clientHeight < 48) loadMore(field);
    }}, noOptionsText:state.error ? "Unable to load options" :
      state.loading || state.key !== requestKey ? "Loading values…" : "No matching values"},
    textFieldProps: {helperText: state.more?.[field] ? "Scroll for more or search by the beginning of a value" : undefined},
  });
  return {options, fieldProps, loading:open && (state.loading || state.key !== requestKey) && !state.error, error:state.error};
}
